<template>
  <v-app :key="selected.aws_resource_name" id="inspire">
    <v-main class="auth-main">
      <v-row no-gutters class="auth-layout-row">
        <v-col
          class="image tw-hidden lg:tw-block image tw-h-auto tw-flex tw-justify-center"
          cols="12"
          sm="12"
          md="6"
          lg="6"
        >
          <div class="tw-w-full tw-h-screen tw-flex tw-justify-center tw-items-end">
            <div class="tw-w-full tw-flex flex-wrap tw-justify-start tw-mt-10 lg:tw-mt-0 tw-px-2 md:tw-px-4 tw-mb-20">
              <div class="tw-w-full tw-flex tw-items-center tw-justify-start tw-text-left">
                <div class="tw-px-2">
                  <h1 class="c-white-text tw-text-xl md:tw-text-2xl lg:tw-text-3xl tw-font-bold tw-leading-tight">
                    {{ heroTitle }}
                  </h1>
                  <h3 class="tw-text-base md:tw-text-lg c-white-text tw-opacity-80 tw-mt-2 tw-font-medium">
                    {{ heroSubtitle }}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <auth-form-panel panel-id="signup" :total-steps="3" :current-step="step">
          <!-- Step 1: Verify ID -->
          <v-form v-if="step === 1" ref="step1" v-model="step1Valid">
            <div class="auth-row-2 tw-mb-4">
              <div>
                <label class="auth-field-label">Country</label>
                <v-text-field
                  value="Kenya"
                  class="auth-input"
                  outlined
                  dense
                  hide-details
                  disabled
                />
              </div>
              <div>
                <label class="auth-field-label">Document type</label>
                <v-select
                  v-model="docType"
                  :items="docTypes"
                  class="auth-input"
                  outlined
                  dense
                  hide-details
                />
              </div>
            </div>

            <label class="auth-field-label" for="id_number">National ID number</label>
            <v-text-field
              id="id_number"
              v-model="form.id_number"
              class="auth-input"
              placeholder="8 or 9 digits"
              outlined
              dense
              hide-details="auto"
              maxlength="9"
              inputmode="numeric"
              :rules="[required('National ID'), nationalIdFormat()]"
              @keyup.enter="verifyId"
              @input="onIdNumberInput"
            />

            <div v-if="idVerified" class="auth-id-verified">
              <v-icon small color="success">mdi-check-circle</v-icon>
              Identity verified. You can continue.
            </div>

            <v-btn
              block
              outlined
              class="auth-btn-pill auth-btn-pill--outline tw-mb-4 tw-mt-2"
              :loading="idVerifying"
              :disabled="!form.id_number"
              @click="verifyId"
            >
              {{ idVerified ? 'Verified' : 'Verify ID' }}
            </v-btn>

            <p class="auth-muted">
              Already have an account?
              <router-link :to="{ name: 'SignIn', query: authLinkQuery }">Log In</router-link>
            </p>

            <v-btn
              block
              class="auth-btn-pill auth-btn-pill--primary tw-mt-4"
              :disabled="!idVerified"
              @click="step = 2"
            >
              Next
            </v-btn>

            <need-help-dialog />
          </v-form>

          <!-- Step 2: Contact -->
          <v-form v-if="step === 2" ref="step2" v-model="step2Valid">
            <label class="auth-field-label" for="mobile">Mobile number</label>
            <v-text-field
              id="mobile"
              v-model="form.mobile"
              class="auth-input tw-mb-4"
              placeholder="e.g. 254712345678"
              outlined
              dense
              hide-details="auto"
              :rules="[required('Mobile No.'), mobileFormat()]"
            />

            <label class="auth-field-label" for="email">Email address</label>
            <v-text-field
              id="email"
              v-model="form.email"
              class="auth-input tw-mb-4"
              type="email"
              placeholder="Enter your email"
              outlined
              dense
              hide-details="auto"
              :rules="[required('Email'), emailFormat()]"
            />

            <div v-if="accountExists" class="auth-account-exists">
              <v-icon small color="warning">mdi-alert-circle</v-icon>
              <span>
                An account with this email already exists.
                <router-link :to="{ name: 'SignIn', query: authLinkQuery }">Sign in</router-link>
                instead.
              </span>
            </div>

            <div class="auth-btn-row">
              <v-btn outlined class="auth-btn-pill auth-btn-pill--outline" @click="step = 1">
                Back
              </v-btn>
              <v-btn
                class="auth-btn-pill auth-btn-pill--primary"
                :disabled="!step2Valid"
                @click="goToStep3"
              >
                Next
              </v-btn>
            </div>
          </v-form>

          <!-- Step 3: Password -->
          <v-form v-if="step === 3" ref="step3" v-model="step3Valid">
            <label class="auth-field-label" for="password">Password</label>
            <v-text-field
              id="password"
              v-model="form.password"
              class="auth-input tw-mb-4"
              :type="passwordField"
              placeholder="Create a password"
              outlined
              dense
              hide-details="auto"
              :rules="[required('Password'), minLength('password', 8), lowerCaseFormat(), upperCaseFormat(), digitFormat(), specialCharFormat()]"
              append-icon="mdi-eye-outline"
              @click:append="passwordField = passwordField === 'password' ? 'text' : 'password'"
            />

            <label class="auth-field-label" for="confirmPassword">Confirm password</label>
            <v-text-field
              id="confirmPassword"
              v-model="form.confirmPassword"
              class="auth-input tw-mb-4"
              :type="passwordConfirmField"
              placeholder="Confirm password"
              outlined
              dense
              hide-details="auto"
              :rules="[required('Confirm Password'), confirmPassword('Confirm Password', form.password)]"
              append-icon="mdi-eye-outline"
              @click:append="passwordConfirmField = passwordConfirmField === 'password' ? 'text' : 'password'"
            />

            <v-checkbox
              v-model="form.terms"
              :rules="[check()]"
              hide-details="auto"
              class="auth-terms-checkbox"
            >
              <template #label>
                <span class="tw-text-sm tw-text-gray-600">
                  I acknowledge that I have read and understood the
                  <a href="#" class="auth-field-link" @click.prevent.stop="openTerms">Terms and Conditions</a>.
                </span>
              </template>
            </v-checkbox>

            <v-checkbox
              v-model="form.privacy"
              :rules="[check()]"
              hide-details="auto"
              class="auth-terms-checkbox"
            >
              <template #label>
                <span class="tw-text-sm tw-text-gray-600">
                  I acknowledge that I have read and understood the
                  <a
                    href="https://www.pacisinsurance.com/privacy"
                    class="auth-field-link"
                    target="_blank"
                    rel="noopener"
                    @click.stop
                  >Privacy Policy</a>.
                </span>
              </template>
            </v-checkbox>
            <terms-and-conditions ref="termsDialog" pre="" post="" class="auth-terms-hidden" />

            <div class="auth-btn-row auth-terms-actions">
              <v-btn outlined class="auth-btn-pill auth-btn-pill--outline" @click="step = 2">
                Back
              </v-btn>
              <v-btn
                id="signUp"
                class="auth-btn-pill auth-btn-pill--primary"
                :loading="loading"
                :disabled="!step3Valid"
                @click="onSignUp"
              >
                Create account
              </v-btn>
            </div>

            <need-help-dialog />
          </v-form>
        </auth-form-panel>
      </v-row>
    </v-main>
  </v-app>
</template>

<script>
import AuthFormPanel from '@/components/auth/AuthFormPanel';
import NeedHelpDialog from '@/components/auth/NeedHelpDialog';
import TermsAndConditions from '@/components/TermsAndConditions';
import validations from '@/utils/validations';
import { normalizeNationalId, validateNationalId } from '@/utils/paValidation';
import axios from 'axios';
import Amplify from '@aws-amplify/core';
import { mapGetters } from 'vuex';
import { FB_SIGNUP, USER_EMAIL } from '@/utils/const';
import AWSResources from '@/utils/aws-exports';
import {
  mergePortalQuery,
  persistPortalPool,
  resolveInitialPool,
} from '@/utils/portalPool';

const { Logger } = Amplify;
const logger = new Logger('SignUpPage');

export default {
  metaInfo: { title: 'Sign Up' },
  components: { AuthFormPanel, NeedHelpDialog, TermsAndConditions },
  data() {
    const customerPool = resolveInitialPool(AWSResources, { query: { portal: 'customer' } });
    return {
      step: 1,
      step1Valid: false,
      step2Valid: false,
      step3Valid: false,
      docType: 'National ID',
      docTypes: ['National ID'],
      idVerified: false,
      idVerifying: false,
      accountExists: false,
      form: {
        mobile: '+254',
        email: '',
        id_number: '',
        password: '',
        confirmPassword: '',
        terms: false,
        privacy: false,
      },
      fullname: '',
      ...validations,
      loading: false,
      passwordField: 'password',
      passwordConfirmField: 'password',
      query: '',
      selected: customerPool,
      kraPin: '',
      configurations: AWSResources,
    };
  },
  created() {
    this.selected = resolveInitialPool(AWSResources, { query: { portal: 'customer' } });
    Amplify.configure(this.selected);
    persistPortalPool(this.selected);
  },
  computed: {
    ...mapGetters('auth', ['hasAuthenticationStatus', 'authenticationStatus']),
    authLinkQuery() {
      return mergePortalQuery(this.$route.query, this.selected);
    },
    heroTitle() {
      return 'Your insurance, your way.';
    },
    heroSubtitle() {
      return 'Buy cover, manage policies, and report claims in minutes.';
    },
  },
  mounted() {
    const quote = this.$route.query.q;
    const benefit = this.$route.query.b;
    if (quote && benefit) this.query = this.$route.query;
    if (this.$route.query.portal === 'agent') {
      this.$toast.info('Agent accounts are created by Pacis. Please sign in or contact support.', 'Agent signup');
      this.$router.replace({ name: 'SignIn', query: { portal: 'agent' } });
      return;
    }
    this.selected = resolveInitialPool(AWSResources, { query: { portal: 'customer' } });
    Amplify.configure(this.selected);
    persistPortalPool(this.selected);
    this.$store.dispatch('auth/clearAuthenticationStatus');
  },
  methods: {
    openTerms() {
      if (this.$refs.termsDialog) this.$refs.termsDialog.dialog = true;
    },
    onIdNumberInput(value) {
      const digits = normalizeNationalId(value);
      if (digits !== this.form.id_number) this.form.id_number = digits;
      this.idVerified = false;
    },
    async verifyId() {
      const idCheck = validateNationalId(this.form.id_number);
      if (!idCheck.valid) {
        this.$toast.error(idCheck.message, 'ID verification');
        return;
      }
      this.form.id_number = idCheck.normalized;
      this.idVerifying = true;
      this.idVerified = false;
      this.fullname = '';
      this.kraPin = '';
      try {
        const idNumber = idCheck.normalized;
        const [iprsRes, kraRes] = await Promise.allSettled([
          axios.get(
            `/customer/gis/policies/iprsdetail?idNumber=${encodeURIComponent(idNumber)}`,
            { _skipErrorRedirect: true, _showErrorToast: false },
          ),
          axios.get(
            `/customer/gis/policies/client-kra?idNumber=${encodeURIComponent(idNumber)}`,
            { _skipErrorRedirect: true, _showErrorToast: false, validateStatus: () => true },
          ),
        ]);
        const response = iprsRes.status === 'fulfilled' ? iprsRes.value : null;
        const row = response?.data?.object?.[0];
        if (!row) {
          this.$toast.error('No record found for this ID. Check the number and try again.', 'IPRS');
          return;
        }
        const name = `${row.firstName || ''} ${row.otherName || ''} ${row.surname || ''}`.trim();
        this.fullname = name;
        if (kraRes.status === 'fulfilled') {
          const kra = kraRes.value?.data?.object;
          if (kra?.ResponseCode === '30000' && kra.TaxpayerPIN) {
            this.kraPin = String(kra.TaxpayerPIN).trim().toUpperCase();
          }
        }
        this.idVerified = true;
        this.$toast.success('Identity verified', 'Success');
      } catch (e) {
        this.$toast.error('Could not verify National ID. Check the number and try again.', 'IPRS');
      } finally {
        this.idVerifying = false;
      }
    },
    goToStep3() {
      if (!this.idVerified) {
        this.$toast.error('Please verify your National ID first', 'Sign up');
        this.step = 1;
        return;
      }
      if (!this.$refs.step2.validate()) return;
      this.accountExists = false;
      this.step = 3;
    },
    async onSignUp() {
      if (!this.idVerified) {
        this.$toast.error('Please verify your National ID first', 'Sign up');
        this.step = 1;
        return;
      }
      if (!this.$refs.step3.validate()) return;

      this.loading = true;
      logger.debug('sign-up');
      this.$cookies.set(USER_EMAIL, this.form.email);
      persistPortalPool(this.selected);
      try {
        await this.$store.dispatch('auth/signUp', {
          username: this.form.email,
          password: this.form.password,
          attributes: {
            name: this.fullname,
            email: this.form.email,
            phone_number: this.form.mobile,
            'custom:id_number': this.form.id_number,
            ...(this.kraPin ? { 'custom:kra-pin': this.kraPin } : {}),
            preferred_username: this.form.email,
          },
        });
      } finally {
        this.loading = false;
      }

      if (!this.hasAuthenticationStatus) return;
      if (this.authenticationStatus.variant === 'error') {
        if (this.authenticationStatus.accountExists) {
          this.accountExists = true;
          this.step = 2;
        }
        this.$toast.error(this.authenticationStatus.message, 'Error');
        return;
      }

      this.$analytics.logEvent(FB_SIGNUP, { email: this.form.email });
      this.$toast.success(this.authenticationStatus.message, 'Success');
      this.$router.push({ name: 'ConfirmOtp', query: this.authLinkQuery });
    },
  },
};
</script>

<style lang="scss" scoped>
.image {
  background-image: url('/images/bg-1.jpg');
  background-repeat: no-repeat;
  background-attachment: cover;
  background-position: center;
  @media only screen and (min-width: 1280px) {
    background-size: cover;
  }
}
</style>
